import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2, Calendar } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { getVendorDisplayName } from "@/utils/vendorHelpers";
import { AwardFeedbackDisplay } from "./AwardFeedbackDisplay";

interface AwardedBidSummaryProps {
  winningBid: any;
  awardData: {
    id: string;
    awarded_by_user_id: string;
    awarded_at: string;
    award_notes?: string;
    total_award_amount_cents: number;
  };
  awardSelections: Array<{
    specification_id: string;
    quantity_option: number;
    unit_price_cents: number;
    total_price_cents: number;
    bid_line_item_id: string;
    tax_cents?: number;
  }>;
  specifications: Array<{ id: string; specification_id: string; name: string }>;
  quantityOptions: Array<{ id: string; specification_id: string; quantity_option: number; unit: string }>;
}

export function AwardedBidSummary({
  winningBid,
  awardData,
  awardSelections,
  specifications,
  quantityOptions,
}: AwardedBidSummaryProps) {
  if (!winningBid || !awardData) return null;

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  return (
    <Card className="border-2 border-green-500 bg-green-50/50 dark:bg-green-950/20 shadow-lg mb-6">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <CardTitle className="text-2xl flex items-center gap-2">
                <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />
                Awarded Bid
              </CardTitle>
              <Badge variant="default" className="bg-green-600 hover:bg-green-700 text-white">
                WINNER
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              This bid was selected and awarded to the vendor
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Vendor Information */}
        <div className="p-4 bg-background rounded-lg border">
          <h4 className="font-semibold text-sm text-muted-foreground mb-2">Awarded Vendor</h4>
          <div className="space-y-1">
            <p className="font-semibold text-lg">
              {getVendorDisplayName(winningBid.vendor_profile)}
            </p>
            {(winningBid.vendor_profile?.first_name || winningBid.vendor_profile?.last_name) && (
              <p className="text-sm text-muted-foreground">
                Contact: {[winningBid.vendor_profile?.first_name, winningBid.vendor_profile?.last_name].filter(Boolean).join(' ')}
              </p>
            )}
          </div>
        </div>

        {/* Awarded Specifications */}
        {awardSelections.length > 0 && (
          <div>
            <h4 className="font-semibold text-sm text-muted-foreground mb-3">Awarded Items</h4>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr className="text-xs">
                    <th className="text-left p-3 font-medium">Specification</th>
                    <th className="text-right p-3 font-medium">Quantity</th>
                    <th className="text-right p-3 font-medium">Unit Price</th>
                    <th className="text-right p-3 font-medium">Total</th>
                    <th className="text-right p-3 font-medium">Est. Tax</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {awardSelections.map((selection, idx) => {
                    const spec = specifications.find(s => s.id === selection.specification_id);
                    const quantityOption = quantityOptions.find(
                      q => q.specification_id === selection.specification_id && 
                           q.quantity_option === selection.quantity_option
                    );
                    
                    return (
                      <tr key={idx} className="text-sm">
                        <td className="p-3 font-medium">{spec?.name || 'Unknown'}</td>
                        <td className="p-3 text-right">
                          {selection.quantity_option} {quantityOption?.unit || 'units'}
                        </td>
                        <td className="p-3 text-right">{formatCurrency(selection.unit_price_cents)}</td>
                        <td className="p-3 text-right font-semibold">{formatCurrency(selection.total_price_cents)}</td>
                        <td className="p-3 text-right text-muted-foreground">
                          {selection.tax_cents && selection.tax_cents > 0 
                            ? formatCurrency(selection.tax_cents)
                            : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Award Notes */}
        {awardData.award_notes && (
          <div className="p-4 bg-background rounded-lg border">
            <h4 className="font-semibold text-sm text-muted-foreground mb-2">Award Notes</h4>
            <p className="text-sm whitespace-pre-wrap">{awardData.award_notes}</p>
          </div>
        )}

        {/* Selection Feedback - visible to winning vendors */}
        <AwardFeedbackDisplay awardId={awardData.id} isVendorView={true} />

        {/* Total Amount */}
        <div className="pt-4 border-t space-y-2">
          {(() => {
            const totalTax = awardSelections.reduce((sum, sel) => sum + (sel.tax_cents || 0), 0);
            return (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Subtotal</span>
                  <span className="text-xl font-semibold">
                    {formatCurrency(awardData.total_award_amount_cents)}
                  </span>
                </div>
                {totalTax > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Est. Tax</span>
                    <span className="text-lg">
                      {formatCurrency(totalTax)}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between pt-2">
                  <span className="text-lg font-semibold">Total Award Amount</span>
                  <span className="text-3xl font-bold text-green-600 dark:text-green-400">
                    {formatCurrency(awardData.total_award_amount_cents + totalTax)}
                  </span>
                </div>
              </>
            );
          })()}
        </div>

        {/* Award Metadata */}
        <div className="flex items-center gap-4 pt-4 border-t text-sm text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <Calendar className="h-4 w-4" />
            <span>
              Awarded {formatDistanceToNow(new Date(awardData.awarded_at), { addSuffix: true })}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
